import { Stack } from "@chakra-ui/react";
import Joi from "joi";
import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";

import {
  roomCount as roomCountConfig,
  houseType as houseTypeConfig,
  colivingType as colivingTypeConfig,
  propertyType as propertyTypeConfig,
  disposition as dispositionConfig,
  offerType as offerTypeConfig,
} from "@ud/config/prodeje/config";
import { updateData } from "@ud/store/insertForm/insertSlice";
import IconDropdown from "components/Chakra/Icons/Dropdown";
import ArrowDown from "components/Chakra/Icons/ArrowDown";
import StepButtons from "components/InsertOfferWrapper/components/StepButtons";
import { useOfferInsert } from "contexts/InsertOffer";
import useForm, { HiddenInput, OnFormSubmitType } from "hooks/useForm";
import useRouter from "hooks/useRouter";
import SectionCards from "../components/SectionCards";
import { SectionTitle } from "../components/SectionTitle";
import { useAppSelector } from "hooks/reduxHooks";
import IconDocument from "components/Chakra/Icons/Document";
import IconHouse from "components/Chakra/Icons/House";
import Select from "components/Chakra/Selectbox";
import { useTracking } from "contexts/Tracking";
import IconPodlazi from "components/Chakra/Icons/Podlazi";
import IconSofa from "components/Chakra/Icons/Sofa";
import SelectInput from "components/Chakra/SelectInput";

type IOfferInfo = {
  buttonNextText?: string;
  buttonPrevText?: string;
};

type IFormValue = {
  typeOffer: string;
  typeProperty: string;
  disposition: string;
  totalRooms: string;
  typeCohabiting: string;
  houseType: string;
};

type CustomFormValue =
  | "typeOffer"
  | "typeProperty"
  | "disposition"
  | "houseType"
  | "typeCohabiting"
  | "totalRooms";

const formSchema = Joi.object({
  typeOffer: Joi.string().required(),
  disposition: Joi.string()
    .when("typeOffer", {
      is: offerTypeConfig.coliving.id,
      then: Joi.allow(""),
      otherwise: Joi.required(),
    })
    .when("typeProperty", {
      is: propertyTypeConfig.house.id,
      then: Joi.allow(""),
      otherwise: Joi.required(),
    }),
  typeProperty: Joi.string().required(),
  typeCohabiting: Joi.string().when("typeOffer", {
    is: offerTypeConfig.coliving.id,
    then: Joi.required(),
    otherwise: Joi.allow(""),
  }),
  totalRooms: Joi.string().when("typeOffer", {
    not: offerTypeConfig.coliving.id,
    then: Joi.when("typeProperty", {
      is: propertyTypeConfig.house.id,
      then: Joi.required(),
      otherwise: Joi.allow(""),
    }),
    otherwise: Joi.allow(""),
  }),
});

const InfoOfferForm: React.FC<IOfferInfo> = ({
  buttonNextText,
  buttonPrevText,
}) => {
  const dispatch = useDispatch();
  const insertReduxData = useAppSelector((e) => e.insertForm.data);
  const { navigateTo } = useRouter();
  const { nextStep, updateStep, getFirstStep } = useOfferInsert();
  const { trackEvent } = useTracking();
  const {
    handleSubmit,
    registerWithError,
    setValue,
    watch,
    errors,
    clearErrors,
  } = useForm<IFormValue>(formSchema, {
    keepDataOnSuccess: true,
    defaultValues: {
      disposition: insertReduxData.disposition ?? "",
      houseType: insertReduxData.houseType ?? houseTypeConfig.familyHouse.id,
      typeOffer: insertReduxData.typeOffer ?? "",
      totalRooms: insertReduxData.totalRooms ?? "",
      typeCohabiting: insertReduxData.typeCohabiting ?? "",
      typeProperty: insertReduxData.typeProperty ?? "",
    },
  });

  const watchTypeOffer = watch("typeOffer");
  const watchDisposition = watch("disposition");
  const watchHouseType = watch("houseType");
  const watchTypeProperty = watch("typeProperty");
  const watchTypeCohabiting = watch("typeCohabiting");
  const watchTotalRooms = watch("totalRooms");
  const dispositionItems = Object.entries(dispositionConfig).map(
    ([, item]) => ({
      id: item.id,
      value: item.translationId,
      label: item.translationId,
    }),
  );
  const selectedDispositionLabel =
    Object.values(dispositionConfig).find(
      (item) => item.id === watchDisposition,
    )?.translationId ?? "";

  const handleData: OnFormSubmitType<IFormValue> = () => {
    dispatch(
      updateData({
        data: {
          disposition: watchDisposition ?? [],
          houseType: watchHouseType ?? [],
          typeOffer: watchTypeOffer || undefined,
          typeProperty: watchTypeProperty || undefined,
          typeCohabiting: watchTypeCohabiting || undefined,
          totalRooms: watchTotalRooms || undefined,
        },
      }),
    );
    updateStep("finished");
    trackEvent({
      "dl.eCat": "offer_create",
      "dl.eAct": "bttn.clicked",
      "dl.eLab": "2. krok – Nabídka",
    });
    const step = nextStep();
    navigateTo(step.url, {
      id: step.query?.id,
      status: step.query?.status,
      paidId: step.query?.paidId,
    });
  };

  const checkIsBtnDisable = (): boolean => {
    const requiredArray: Array<string | Array<string> | number | undefined> = [
      watchTypeOffer,
      watchTypeProperty,
    ];
    if (watchTypeOffer === offerTypeConfig.coliving.id) {
      requiredArray.push(watchTypeCohabiting);
    }
    if (
      watchTypeOffer !== offerTypeConfig.coliving.id &&
      watchTypeProperty === propertyTypeConfig.flat.id
    ) {
      requiredArray.push(watchDisposition);
    }

    if (
      watchTypeOffer !== offerTypeConfig.coliving.id &&
      watchTypeProperty === propertyTypeConfig.house.id
    ) {
      requiredArray.push(watchTotalRooms);
      requiredArray.push(watchHouseType);
    }

    return requiredArray.some((item) => !item);
  };

  const handleSetValue = useCallback(
    (name: CustomFormValue, value: string) => {
      setValue(name, value);
    },
    [setValue],
  );

  useEffect(() => {
    if (!insertReduxData.typeInsert) {
      const step = getFirstStep();
      navigateTo(step.url, {
        id: step.query?.id,
        status: step.query?.status,
        paidId: step.query?.paidId,
      });
    }
  }, []);

  useEffect(
    () =>
      trackEvent({
        "dl.eCat": "offer_create",
        "dl.eAct": "page.loaded",
        "dl.eLab": "2. krok – Nabídka",
      }),
    [],
  );

  return (
    <form
      onSubmit={handleSubmit(handleData)}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Stack textAlign="left" spacing={0}>
        <SectionCards
          title="Typ nabídky"
          setValue={handleSetValue}
          formWatch={watchTypeOffer}
          nameValue="typeOffer"
          items={offerTypeConfig}
          isRequired
          titlePaddingProps={{ pt: "0", pb: "16px" }}
          icon={<IconDocument boxSize="16px" fillWith="ud-grey-600" />}
          isDisabled={!!insertReduxData.offerId}
        />
        {watchTypeOffer && (
          <SectionCards
            title="Typ nemovitosti"
            setValue={handleSetValue}
            formWatch={watchTypeProperty}
            numberOfColumns={{
              desktop: 2,
              mobile: 2,
            }}
            nameValue="typeProperty"
            items={propertyTypeConfig}
            icon={<IconPodlazi boxSize="16px" />}
            isRequired
            isDisabled={!!insertReduxData.offerId}
          />
        )}
        {watchTypeOffer &&
          watchTypeOffer !== offerTypeConfig.coliving.id &&
          watchTypeProperty === propertyTypeConfig.flat.id && (
            <>
              <SectionTitle
                title="Dispozice"
                isRequired
                icon={<IconHouse boxSize="16px" />}
              />
              <HiddenInput {...registerWithError("disposition")} />
              <SelectInput
                name="disposition"
                placeholder="Vyberte dispozici"
                extValue={selectedDispositionLabel}
                items={dispositionItems}
                onSelectItem={({ id }) => {
                  setValue("disposition", id, { shouldValidate: true });
                  clearErrors("disposition");
                }}
                error={errors.disposition}
                icon={<ArrowDown fillWith="ud-grey-600" boxSize="24px" />}
                isReadOnly
                dropdownMaxHeight="220px"
              />
            </>
          )}
        {watchTypeOffer &&
          watchTypeOffer !== offerTypeConfig.coliving.id &&
          watchTypeProperty === propertyTypeConfig.house.id && (
            <>
              <SectionTitle
                title="Typ domu"
                isRequired
                icon={<IconHouse boxSize="16px" />}
              />
              <Select
                backgroundColor="ud-white"
                {...registerWithError("houseType")}
                icon={<IconDropdown />}
                iconSize="12px"
                defaultValue={houseTypeConfig.familyHouse.translationId}
              >
                {Object.entries(houseTypeConfig).map((item) => (
                  <option key={item[0]} value={item[1].id}>
                    {item[1].translationId}
                  </option>
                ))}
              </Select>
            </>
          )}
        {watchTypeProperty &&
          watchTypeOffer === offerTypeConfig.coliving.id && (
            <SectionCards
              title="Typ spolubydlení"
              setValue={handleSetValue}
              formWatch={watchTypeCohabiting}
              nameValue="typeCohabiting"
              numberOfColumns={{
                desktop: 2,
                mobile: 2,
              }}
              items={colivingTypeConfig}
              icon={<IconSofa boxSize="16px" />}
              titlePaddingProps={{ pt: "40px", pb: "16px" }}
              isRequired
            />
          )}
        {watchTypeProperty === propertyTypeConfig.house.id &&
          watchTypeOffer !== offerTypeConfig.coliving.id && (
            <SectionCards
              title="Počet pokojů"
              setValue={handleSetValue}
              formWatch={watchTotalRooms}
              nameValue="totalRooms"
              items={roomCountConfig}
              icon={<IconSofa boxSize="16px" />}
              isRequired
              titlePaddingProps={{ pt: "24px", pb: "16px" }}
            />
          )}
      </Stack>
      <StepButtons
        checkIsBtnDisable={checkIsBtnDisable}
        buttonPrevText={buttonPrevText}
        buttonNextText={buttonNextText}
        mt={[null, "24px"]}
      />
    </form>
  );
};

export default InfoOfferForm;
